import { cloneDeep, throttle, values } from 'lodash'

import * as Store from './store'
import {
  getLastThreads,
  setThreadPinnedValue,
  setThreadDeletedValue,
  getLastThreadsByFilter,
  getSubthreadsForThread,
  getThreadById,
} from './api'
import boxesService from '@/app-domains/boxes/boxes.service'
import FiltersHandler from '@/app-domains/filters/filters.handler'
import labelsService from '../labels/labels.service'
import DraftManagerService from '../messages/draft/service/DraftManager.service'
import store from '@/core/store'
import ToastService from '@/components/Toast/Toast.service'
import messagesService from '../messages/messages.service'
import sessionService from '../user/session/session.service'

class ThreadsService {
  constructor () {
    this.filterHandler = new FiltersHandler({
      scope: 'threads',
      fetchDataFromAPI: this.fetchDataFromAPI.bind(this),
      storeHandlers: { ...Store },
      filterProcessor: this.getThreadsForFilter.bind(this),
      events: {
        localFetch: 'FILTERS:UPDATED',
        afterAPIFetch: 'FILTERS:UPDATED'
      }
    })
    this.pendingQueries = []
    this.applyThrottledCurrentFilterOnNextPage = throttle(this.applyCurrentFilterOnNextPage, 4000)
  }

  switchFilterUnreadOrReadMode (unread) {
    Store.setFilterMode(unread)
    this.reapplyCurrentFilter()
  }

  fetchDataFromAPI (filter, page) {
    if (!filter.type || filter.type === 'all') {
      return this.queryLastThreads({ page })
    }

    return this.queryLastThreadsByFilter(
      {
        type: filter.type,
        value: filter.value,
      }, page
    )
  }

  setFilterMode (unread = false) {
    Store.setFilterMode(unread)
  }

  get isQueryingForThreads () {
    return this.pendingQueries.length > 0
  }

  addPendingQuery (data) {
    this.pendingQueries.push(JSON.stringify(data))
  }

  removePendingQuery (data) {
    this.pendingQueries = this.pendingQueries.filter(query => query !== JSON.stringify(data))
  }

  async queryLastThreads (data, onlyVisible = true) {
    const filterMode = Store.getFilterMode()
    const page = data?.page || 0
    this.addPendingQuery(data)
    let res

    try {
      res = await getLastThreads(page, onlyVisible, filterMode)
      if (res.threads) {
        Store.setStoredThreads(res.threads)
        await this.processThreadsQueryResult(res)
      }
    } catch (e) {

    } finally {
      this.removePendingQuery(data)
    }

    return res
  }

  async queryLastThreadsByFilter (filter, page = 0) {
    const filterMode = Store.getFilterMode()
    this.addPendingQuery(filter)
    let res
    
    try {
      res = await getLastThreadsByFilter(filter, filterMode, page)
      if (res.threads) {
        Store.setStoredThreads(res.threads)
        await this.processThreadsQueryResult(res)
      }
    } catch (e) {

    } finally {
      this.removePendingQuery(filter)
    }
    
    return res
  }

  getBaseFilterForMainFeed () {
    return {
      type: 'all',
      value: 'all',
      label: 'All Messages',
    }
  }
  
  async applyFilterFor (type, label, value, forceFetch = true) {
    // TODO: Move scrollable outside into a helper service
    const scrollableElement = document.getElementById('main-feed-scroll')
    if (scrollableElement) { scrollableElement.scrollTop = 0 }
    
    await this.filterHandler.applyFilterFor(type, label, value, forceFetch)
  }

  async applyCurrentFilterOnNextPage () {
    const nextPage = Store.getNextPageForCurrentFilter()
    if (!nextPage || Store.getLoadingStatus()) return
    const currentFilter = Store.getFilter()
    const data = await this.applyFilter(currentFilter)
    return data
  }

  async reapplyCurrentFilter () {
    const filter = this.getCurrentFilter()
    await this.applyFilter(filter, true, true)
  }

  getCurrentFilter () {
    return Store.getFilter()
  }

  async applyFilter (filter, clearFilterPage = false, forceFetch = false) {
    const filterPage = Store.getFilterPageForFilter(filter)
    if (!clearFilterPage && filterPage && filterPage.page >= 0 && filterPage.next > filterPage.page) {
      const data = await this.filterHandler.applyFilter(filter, filterPage.next, false, forceFetch)
      return data
    } else {
      const data = await this.filterHandler.applyFilter(filter, 0, Store.getFilterMode() !== 'unread' && filter.value !== 'all', forceFetch)
      return data
    }
  }

  retrieveFilteredData () {
    const currentFilter = Store.getFilter()
    return this.getThreadsForFilter(currentFilter)
  }

  queryThreadsByResettingCurrentFilterPage () {
    const currentFilter = Store.getFilter()
    this.applyFilter(currentFilter, true, true)
  }

  async queryThreadById (id) {
    const thread = await getThreadById(id)
    Store.setStoredThreads({ [thread.composedid]: thread })
    return thread
  }

  getThreadsForFilter (filter) {
    let allThreads = values(this.getThreads())

    if (Store.getFilterMode() === 'unread') {
      allThreads = allThreads.filter(thread => this.getHeadMessageOfThread(thread).unopen)
    }

    const nonDeletedThreads = allThreads.filter(thread => !thread.isDeleted)

    if (!filter || !filter.type || filter.type === 'all') {
      return this.getThreadsForEverythingTab(allThreads)
    }

    const filters = {
      text: (value) => this.getThreadsByText(nonDeletedThreads, value),
      box: (value) => this.getThreadsForBox(nonDeletedThreads, value),
      label: (value) => this.getThreadsForLabel(nonDeletedThreads, value),
      pinned: () => this.getPinnedThreads(nonDeletedThreads),
      promotional: () => this.getPromotionalThreads(nonDeletedThreads),
      spam: () => this.getSpamThreads(nonDeletedThreads),
      deleted: () => this.getDeletedThreads(allThreads),
      sent: () => this.getSentThreads(allThreads),
      archived: () => this.getArchivedBoxesThreads(allThreads),
      'no-label': () => this.getThreadsForNoLabeled(allThreads)
    }
    
    const filterFn = filters[filter.type.toLowerCase()]
    const filteredThreads = filterFn(filter.value)
    return filteredThreads
  }

  getArchivedBoxesThreads (allThreads) {
    const archivedBoxesIds = boxesService.getAllStoredBoxes().filter(box => box.archived).map((box) => box.composedid)
    const archivedBoxesThreads = allThreads.filter((thread) => archivedBoxesIds.indexOf(thread.boxId) !== -1)
    return archivedBoxesThreads
  }

  getThreadsForEverythingTab (allThreads) {
    if (!allThreads) return []
    const noDeletedThreads = allThreads.filter((thread) => thread && !thread.isDeleted)
    const filteredThreads = noDeletedThreads.filter((thread) => {
      const box = boxesService.findBoxbyId(thread.boxId)
      if (box.archived) { return false }
      const labels = labelsService.findLabelsForBox(box)
      const shouldShowBoxesWithNoLabel = labels.length === 0 && sessionService.getMe().me.preferences?.isUnlabeledShown
      return labels.filter((label) => label.showninfeed).length > 0 || shouldShowBoxesWithNoLabel
    })
    return filteredThreads
  }

  getThreadsForNoLabeled (allThreads) {
    if (!allThreads) return []
    const allLabels = labelsService.getLabelsList()
    const noLabeledLabel = allLabels.filter((l) => l.role === 'default')
    const noDeletedThreads = allThreads.filter((thread) => thread && !thread.isDeleted)
    const filteredThreads = noDeletedThreads.filter((thread) => {
      return noLabeledLabel.filter(
        (label) =>
          label.boxes.find(
            box => box.composedid === thread.boxId && !box.archived
          )
      ).length > 0
    })
    return filteredThreads
  }

  getThreadsForLabel (allThreads, labelId) {
    const boxesIds = boxesService.getBoxesForLabel(labelId)
    const threadsResult = []
    for (const boxId of boxesIds) {
      threadsResult.push(
        ...this.getThreadsForBox(allThreads, boxId)
      )
    }

    return threadsResult
  }

  getThreadsByText (allThreads, value) {
    return allThreads.filter((thread) => {
      const headMessage = this.getHeadMessageOfThread(thread)
      return (
        headMessage.subject?.toLowerCase().indexOf(value) !== -1 ||
        headMessage.text?.toLowerCase().indexOf(value) !== -1 ||
        headMessage.fromEmailRaw?.toLowerCase().indexOf(value) !== -1 ||
        headMessage.boxName?.toLowerCase().indexOf(value) !== -1
      )
    })
  }

  getThreadsForBox (allThreads, boxId) {
    return allThreads.filter((thread) => thread && thread.boxId === boxId)
  }

  getPinnedThreads (allThreads) {
    return allThreads.filter((thread) => thread && thread.isPinned)
  }

  getSentThreads (allThreads) {
    return allThreads.filter((thread) => thread && !thread.originIncoming)
  }

  getDeletedThreads (allThreads) {
    return allThreads.filter((thread) => thread && thread.isDeleted)
  }

  getSpamThreads (allThreads) {
    return allThreads.filter((thread) => thread && thread.isSpam)
  }

  getPromotionalThreads (allThreads) {
    return allThreads.filter((thread) => thread && thread.isPromotional)
  }
   
  getThreads () {
    return Store.getThreads()
  }

  getThreadById (id) {
    return Store.getThreads()[id]
  }

  getLastThread () {
    return values(this.getThreads()).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
  }

  isMessageTheRootMessageOfTheThread (response, thread) {
    return response.isHeadOfThread && (response.threadId === thread.composedid)
  }

  getHeadMessageOfThread (thread) {
    return thread.messages.filter((message) => message.isHeadOfThread)[0]
  }

  async processThreadsQueryResult (res) {
    const enrichedThreads = {}
    const subThreadPromises = []

    Object.values(res.threads).map((thread) => {
      if (thread.maxNestingLevel > 0) {
        const promise = getSubthreadsForThread(thread.composedid).then((res2) => {
          enrichedThreads[thread.composedid] = {
            ...res.threads[thread.composedid],
            subthreads: res2.threads,
          }
        })
        subThreadPromises.push(promise)
      }
      return true
    })

    Promise.all(subThreadPromises).then(() => {
      const threads = cloneDeep({
        ...res.threads,
        ...enrichedThreads,
      })
      if (Object.values(enrichedThreads).length > 0) { // only store threads if there are subthreads
        Store.setStoredThreads(threads)
      }
    })
  }

  async sendReplyMessage (parentMessage, thread) {
    const threadId = parentMessage.threadId
    const replyMessage = await DraftManagerService.sendMessageInstantly(parentMessage.messageid)
    Store.addReplyMessageToThread(threadId, replyMessage, thread)
    store.commit(`THREAD:${threadId}:UPDATED`)
  }

  addReplyMessageToThread (threadId, replyMessage) {
    Store.addReplyMessageToThread(threadId, replyMessage)
  }

  async setThreadPinned (threadId, value) {
    await setThreadPinnedValue(threadId, value)
    const obj = cloneDeep(Store.getThreads())
    obj[threadId].isPinned = value
    Store.setStoredThreads(obj)
  }

  markThreadAsDeleted (threadId, value) {
    Store.markThreadAsDeleted(threadId, value)
  }

  async setThreadDeleted (threadId, message, value, modifyStore = true) {
    if (message.unopen) {
      messagesService.markMessageAsRead(message.id, threadId, message.boxid)
    }
    if (modifyStore) this.markThreadAsDeleted(threadId, value)
    const unopenedCount = this.getUnopenedMessagesForThread(message.threadId)
    if (value) {
      boxesService.decreaseUnopenedCountOfBoxBy(message.boxid, unopenedCount)
    } else {
      boxesService.increaseUnopenedCountOfBoxBy(message.boxid, unopenedCount)
    }
    try {
      await setThreadDeletedValue(threadId, value)
      if (value) this.showUndoDeleteButton(threadId, message)
      await this.preventEmptyFeedByQueryingForCurrentActiveFilter()
    } catch (e) {
      Store.markThreadAsDeleted(!value)
    }
  }

  getUnopenedMessagesForThread (threadId) {
    const thread = Store.getThreads()[threadId]
    return thread?.messages.filter((message) => {
      return message?.unopen
    }).length
  }

  async preventEmptyFeedByQueryingForCurrentActiveFilter () {
    if (this.retrieveFilteredData()?.length < 5) {
      await this.queryThreadsByResettingCurrentFilterPage()
    }
  }

  showUndoDeleteButton (threadId, message) {
    ToastService.show({
      title: 'Message deleted',
      type: 'info',
      message: 'A message was successfully deleted',
      buttonText: 'Undo',
      mobileTapText: 'Tap here to undo',
      onClick: () => {
        this.setThreadDeleted(threadId, message, false)
      }
    })
  }

  markMessageOfThreadAsRead (threadId, messageId) {
    Store.markMessageOfThreadAsRead(threadId, messageId)
  }

  setFilter (filter) {
    Store.setFilter(filter)
  }

  applyBaseFilter () {
    const baseFilter = this.getBaseFilterForMainFeed()
    this.applyFilter(baseFilter, true)
  }

  isFilteringLoading () {
    return Store.getLoadingStatus()
  }

  setViewType (type) {
    Store.setViewType(type)
  }
}

export default new ThreadsService()
